import { AlertTriangle, Clock3, Sparkles, UserRound } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import {
  RESOLVED_LABELS,
  resolvedFieldValue,
} from '@/features/deal-review/deal-lead-views'
import { MUTED_TEXT } from '@/features/deal-review/review-tones'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import type { DealLeadDetail } from '@/types/api'
import { SectionTitle } from '@/components/custom/deal-review/panel-primitives'

type ActorType = DealLeadDetail['activities'][number]['actorType']

type ActivityType = DealLeadDetail['activities'][number]['eventType']

// The response carries only the actor's id, so the type is what can be shown.
const ACTOR_LABELS: Record<ActorType, string> = {
  AI: 'AI',
  USER: 'Reviewer',
  SYSTEM: 'System',
}

const EVENT_DESCRIPTIONS: Record<ActivityType, string> = {
  MESSAGE_CAPTURED: 'Email captured',
  PROCESSING_STARTED: 'Processing started',
  PROCESSING_FAILED: 'Processing failed',
  PROCESSING_RETRIED: 'Processing retried',
  CANDIDATE_CREATED: 'Lead identified in the message',
  LEADS_MERGED: 'Duplicate candidates merged into this lead',
  EXTRACTION_COMPLETED: 'Fields extracted from the source',
  SCREENING_COMPLETED: 'Screened against the criteria',
  FIELDS_CORRECTED: 'Fields corrected by a reviewer',
  NOTE_SAVED: 'Note written by a reviewer',
  LEAD_ARCHIVED: 'Archived or restored by a reviewer',
  DISPOSITION_SAVED: 'Decision recorded',
  DEALCLOUD_SEND_STARTED: 'Send to DealCloud started',
  DEALCLOUD_SEND_SUCCEEDED: 'Sent to DealCloud',
  DEALCLOUD_SEND_FAILED: 'DealCloud send failed',
  DEALCLOUD_SEND_DUPLICATE: 'Set aside as a duplicate',
}

const ACTOR_MARKER: Record<ActorType, string> = {
  AI: 'bg-secondary/10 text-secondary',
  USER: 'bg-emerald-50 text-emerald-700',
  SYSTEM: 'bg-primary/5 text-primary',
}

const ACTOR_ICON: Record<ActorType, LucideIcon> = {
  AI: Sparkles,
  USER: UserRound,
  SYSTEM: Clock3,
}

const FAILURE_EVENTS = new Set<ActivityType>([
  'PROCESSING_FAILED',
  'DEALCLOUD_SEND_FAILED',
])

type SendResult = NonNullable<
  DealLeadDetail['activities'][number]['sendResult']
>

// A duplicate's error is the reviewer-facing reason it was set aside, not a failure.
function SendActivityDetail({
  result,
  failed,
}: {
  result: SendResult
  failed: boolean
}) {
  const { entryId, operation, resolved, omitted, error } = result
  const resolutions = resolved ? Object.entries(resolved) : []

  if (!error && !entryId && resolutions.length === 0 && omitted.length === 0) {
    return null
  }

  return (
    <div className="mt-1 space-y-1">
      {error && (
        <Typography
          variant="body-sm"
          className={failed ? 'text-destructive' : MUTED_TEXT}
        >
          {error}
        </Typography>
      )}
      {entryId && (
        <Typography variant="body-sm" className="text-primary/70">
          {operation
            ? `Deal ${entryId}, ${operation.toLowerCase()}d`
            : `Deal ${entryId}`}
        </Typography>
      )}
      {resolutions.length > 0 && (
        <Typography variant="body-sm" className="text-primary/70">
          {resolutions
            .map(
              ([key, how]) =>
                `${RESOLVED_LABELS[key] ?? key} ${resolvedFieldValue(how)}`
            )
            .join(', ')}
        </Typography>
      )}
      {omitted.length > 0 && (
        <Typography variant="body-sm" className="text-primary/70">
          Left empty: {omitted.join('; ')}
        </Typography>
      )}
    </div>
  )
}

function formatActivityTimestamp(value: string): string {
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// Fixed size-7 + self-start: a marker sized by its row stretches into an oval when it wraps.
export function ActivitySection({ lead }: { lead: DealLeadDetail }) {
  if (lead.activities.length === 0) return null

  return (
    <section>
      <SectionTitle>Activity</SectionTitle>
      <div className="max-w-lg">
        {lead.activities.map((activity, index) => {
          const failed = FAILURE_EVENTS.has(activity.eventType)
          const Icon = failed ? AlertTriangle : ACTOR_ICON[activity.actorType]

          return (
            <div
              key={activity.id}
              data-deal-activity-tone={failed ? 'failure' : 'neutral'}
              className="relative flex items-start gap-3 pb-5 last:pb-0"
            >
              {index < lead.activities.length - 1 && (
                <div
                  data-deal-activity-connector
                  className="absolute bottom-0 left-[13.5px] top-7 w-px bg-primary/15"
                />
              )}
              <div
                data-deal-activity-marker
                className={cn(
                  'relative z-10 flex size-7 shrink-0 self-start items-center justify-center rounded-full',
                  failed
                    ? 'bg-red-50 text-destructive'
                    : ACTOR_MARKER[activity.actorType]
                )}
              >
                <Icon aria-hidden="true" className="size-3.5" />
              </div>
              <div className="min-w-0 pt-0.5">
                <Typography
                  variant="body"
                  className={cn(
                    'text-sm font-bold leading-[1.4]',
                    failed ? 'text-destructive' : 'text-primary/90'
                  )}
                >
                  {EVENT_DESCRIPTIONS[activity.eventType]}
                </Typography>
                {activity.sendResult && (
                  <SendActivityDetail
                    result={activity.sendResult}
                    failed={failed}
                  />
                )}
                <Typography
                  variant="body-sm"
                  className="mt-1 text-xs/[1.2] tabular-nums text-primary/70"
                >
                  {formatActivityTimestamp(activity.createdAt)} ·{' '}
                  {ACTOR_LABELS[activity.actorType]}
                </Typography>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
